import { Listbox, Transition } from '@headlessui/react'
import {
  CheckIcon,
  ChevronUpDownIcon,
  EllipsisVerticalIcon,
} from '@heroicons/react/24/solid'
import classNames from 'classnames'
import React, { Fragment, useRef, useState } from 'react'
import { useDrag, useDrop, XYCoord } from 'react-dnd'
import {
  Controller,
  FieldArrayWithId,
  UseFormRegister,
  useFormState,
  useWatch,
} from 'react-hook-form'
import Trash from '../../../../../components/icons/trash'
import { AnyForm, NumberInput } from '../../../../../components/number-input'
import SlideOver from '../../../../../components/side-popup'
import { AdminPartisanChartFormValues } from '../../../../../forms/charts/admin/admin-chart-partisan-form'
import { AdminManagePartisanChartState } from '../../hooks/use-admin-manage-partisan-chart'
import AdminManagePartisanChartCandidateNamesItem from './admin-manage-partisan-chart-candidate-names'
import AdminManagePartisanNonPartisanChartCandidateNamesItem from './admin-manage-partisan-non-partisan-candidate-names'

interface AdminManagePartisanChartRaceItemProps {
  index: number
  field: FieldArrayWithId<AdminPartisanChartFormValues, 'races', 'id'>
  addNewRace: (name?: any, partyName?: any) => void
  adminManagePartisanChartState: AdminManagePartisanChartState
  candidatesNamesChecked: boolean
}

const AdminManagePartisanChartRaceItem: React.FC<
  AdminManagePartisanChartRaceItemProps
> = ({
  index,
  field,
  adminManagePartisanChartState,
  addNewRace,
  candidatesNamesChecked,
}) => {
  const {
    moveRace,
    removeRace,
    changeRaceType,
    // raceTypes,
    form: { control, register, getValues },
  } = adminManagePartisanChartState

  const { errors } = useFormState({ control, name: 'races' })
  const races = useWatch({ control, name: 'races' })
  const parties = useWatch({ control, name: 'parties' })

  const ref = useRef<HTMLDivElement>(null)
  const [{ handlerId }, drop] = useDrop({
    accept: 'CHART-RACE',
    collect(monitor) {
      return { handlerId: monitor.getHandlerId() }
    },
    hover(item: any, monitor) {
      if (!ref.current) return
      const dragIndex = item.index
      const hoverIndex = index
      if (dragIndex === hoverIndex) return
      const hoverBoundingRect = ref.current?.getBoundingClientRect()
      const hoverMiddleY =
        (hoverBoundingRect.bottom - hoverBoundingRect.top) / 2
      const clientOffset = monitor.getClientOffset()
      const hoverClientY = (clientOffset as XYCoord).y - hoverBoundingRect.top
      if (dragIndex < hoverIndex && hoverClientY < hoverMiddleY) return
      if (dragIndex > hoverIndex && hoverClientY > hoverMiddleY) return
      moveRace(dragIndex, hoverIndex)
      item.index = hoverIndex
    },
  })

  const [, drag] = useDrag(() => ({
    type: 'CHART-RACE',
    item: () => {
      return { id: field.id, index }
    },
    collect: () => ({}),
  }))

  drag(drop(ref))

  const getCountAllFields = () => {
    const formData = getValues()
    let sum = 0
    formData.races[index].parties.forEach((party) => {
      if (party.candidates) {
        sum += +party.candidates
      }
    })
    return sum
  }

  const getCountNamesAdded = () => {
    let sum = 0
    races[index].parties.forEach((party) => {
      party.candidateNames &&
        party.candidateNames?.forEach((candidate) => {
          if (candidate?.name && candidate?.name.length) {
            sum += 1
          }
        })
    })
    return sum
  }

  const getCountNonPartisanNamesAdded = () => {
    let sum = 0
    const candidateNames = getValues().races[index]?.candidateNames ?? []
    candidateNames?.forEach((candidate) => {
      if (candidate.name && candidate.name.length) {
        sum += 1
      }
    })
    return sum
  }

  const [isHeadlessOpen, setIsHeadlessOpen] = useState(false)

  return (
    <>
      <div
        ref={ref}
        data-handler-id={handlerId}
        className="relative flex flex-col w-full p-5 mb-8 bg-white rounded-md shadow-md"
      >
        <div className="flex">
          <div className="absolute -right-6 -top-6">
            <button
              className="p-3 bg-gray-200 rounded-full"
              onClick={() => removeRace(index)}
            >
              <Trash className="w-6 h-6" />
            </button>
          </div>
          <div className="flex self-center opacity-25 cursor-move">
            <EllipsisVerticalIcon className="h-10" />
            <EllipsisVerticalIcon className="h-10 -ml-7" />
          </div>
          <div className="mr-4">
            <div className="flex items-center justify-center w-8 h-8 mt-2 text-white bg-red-500 rounded-full">
              <div>{index + 1}</div>
            </div>
          </div>
          <div className="flex flex-wrap gap-4">
            <div className="flex flex-col w-[240px]">
              <input
                {...register(`races.${index}.name`)}
                type="text"
                placeholder="Race Name"
                className={classNames('py-3 px-4 bg-[#E6EBF5] rounded-md', {
                  'outline outline-2 outline-red-500 focus:outline-red-500':
                    errors?.races?.[index]?.name,
                })}
              />
              <label
                htmlFor="name"
                className="text-[#757575] font-light text-sm mt-1"
              >
                {errors?.races?.[index] ? (
                  <span className="text-[#FF0000]">
                    {errors?.races?.[index]?.name?.message}
                  </span>
                ) : (
                  'Race Name'
                )}
              </label>
            </div>
            <div className="flex flex-col w-[240px]">
              <Controller
                control={control}
                name={`races.${index}.type`}
                render={({ field: { value, onChange } }) => (
                  <Listbox value={value} onChange={onChange}>
                    <div className="relative h-[48px]">
                      <Listbox.Button
                        className={classNames(
                          'relative w-full h-full py-3 px-4 bg-[#E6EBF5] rounded-md text-left cursor-default',
                          {
                            'outline outline-2 outline-red-500 focus:outline-red-500':
                              errors?.races?.[index]?.type,
                          }
                        )}
                      >
                        <span className="block truncate">{value}</span>
                        <span className="absolute inset-y-0 right-0 flex items-center pr-2 pointer-events-none">
                          <ChevronUpDownIcon
                            className="w-5 h-5 text-gray-400"
                            aria-hidden="true"
                          />
                        </span>
                      </Listbox.Button>
                      <Transition
                        as={Fragment}
                        leave="transition ease-in duration-100"
                        leaveFrom="opacity-100"
                        leaveTo="opacity-0"
                      >
                        <Listbox.Options className="absolute z-10 w-full py-1 mt-1 overflow-auto text-base bg-white rounded-md shadow-lg max-h-60 ring-1 ring-black ring-opacity-5 focus:outline-none sm:text-sm">
                          {['Race', 'Proposal'].map((count, i) => (
                            <Listbox.Option
                              key={`type-${index}-${i}`}
                              className={({ active }) =>
                                `cursor-default select-none relative py-2 pl-10 pr-4 ${
                                  active
                                    ? 'text-blue-900 bg-blue-100'
                                    : 'text-gray-900'
                                }`
                              }
                              value={count}
                            >
                              {({ selected }) => (
                                <>
                                  <span
                                    className={`block truncate ${
                                      selected ? 'font-medium' : 'font-normal'
                                    }`}
                                  >
                                    {count}
                                  </span>
                                  {selected ? (
                                    <span className="absolute inset-y-0 left-0 flex items-center pl-3 text-blue-600">
                                      <CheckIcon
                                        className="w-5 h-5"
                                        aria-hidden="true"
                                      />
                                    </span>
                                  ) : null}
                                </>
                              )}
                            </Listbox.Option>
                          ))}
                        </Listbox.Options>
                      </Transition>
                    </div>
                  </Listbox>
                )}
              />
              <label
                htmlFor="type"
                className="text-[#757575] font-light text-sm mt-1"
              >
                Type
              </label>
            </div>
            {races?.[index].type === 'Race' && (
              <div className="flex flex-col w-[240px]">
                <Controller
                  control={control}
                  name={`races.${index}.raceType`}
                  render={({ field: { value, onChange } }) => (
                    <Listbox
                      value={value}
                      onChange={(e) => {
                        onChange(e)
                        changeRaceType(
                          races[index].name,
                          e,
                          races[index].type,
                          index
                        )
                      }}
                    >
                      <div className="relative h-[48px]">
                        <Listbox.Button
                          className={classNames(
                            'relative w-full h-full py-3 px-4 bg-[#E6EBF5] rounded-md text-left cursor-default',
                            {
                              'outline outline-2 outline-red-500 focus:outline-red-500':
                                errors?.races?.[index]?.raceType,
                            }
                          )}
                        >
                          <span className="block truncate">{value}</span>
                          <span className="absolute inset-y-0 right-0 flex items-center pr-2 pointer-events-none">
                            <ChevronUpDownIcon
                              className="w-5 h-5 text-gray-400"
                              aria-hidden="true"
                            />
                          </span>
                        </Listbox.Button>
                        <Transition
                          as={Fragment}
                          leave="transition ease-in duration-100"
                          leaveFrom="opacity-100"
                          leaveTo="opacity-0"
                        >
                          <Listbox.Options className="absolute z-10 w-full py-1 mt-1 overflow-auto text-base bg-white rounded-md shadow-lg max-h-60 ring-1 ring-black ring-opacity-5 focus:outline-none sm:text-sm">
                            {[
                              { id: 1, name: 'Non-Partisan' },
                              ...parties.filter((v) => v.selected),
                            ].map((party, i) => (
                              <Listbox.Option
                                key={`type-${index}-${i}`}
                                className={({ active }) =>
                                  `cursor-default select-none relative py-2 pl-10 pr-4 ${
                                    active
                                      ? 'text-blue-900 bg-blue-100'
                                      : 'text-gray-900'
                                  }`
                                }
                                value={party.name}
                              >
                                {({ selected }) => (
                                  <>
                                    <span
                                      className={`block truncate ${
                                        selected ? 'font-medium' : 'font-normal'
                                      }`}
                                    >
                                      {party.name}
                                    </span>
                                    {selected ? (
                                      <span className="absolute inset-y-0 left-0 flex items-center pl-3 text-blue-600">
                                        <CheckIcon
                                          className="w-5 h-5"
                                          aria-hidden="true"
                                        />
                                      </span>
                                    ) : null}
                                  </>
                                )}
                              </Listbox.Option>
                            ))}
                          </Listbox.Options>
                        </Transition>
                      </div>
                    </Listbox>
                  )}
                />
                <label
                  htmlFor="type"
                  className="text-[#757575] font-light text-sm mt-1"
                >
                  Party
                </label>
              </div>
            )}
            {races[index].type === 'Race' &&
            races[index].raceType !== 'Non-Partisan' ? (
              <>
                {races[index].parties.map((party, partyIdx) => (
                  <>
                    <div className="w-[2px] bg-[#E6EBF5] h-12" />
                    <div className="flex flex-col w-[84px]">
                      <NumberInput
                        name={`races.${index}.candidates`}
                        register={register as UseFormRegister<AnyForm>}
                        id={`${party.name}-${index}-${partyIdx}`}
                        defaultValue={0}
                        disabled={getValues().showCandidateNames}
                        className="w-full h-12 focus:outline-none text-lg px-4 py-3 rounded-md bg-[#E6EBF5] leading-5 text-gray-900 focus:ring-0 disabled:opacity-25 disabled:cursor-not-allowed text-center"
                      />
                      <label
                        htmlFor={`${party.name}-${index}-${partyIdx}`}
                        className="text-[#757575] font-light text-sm mt-1"
                      >
                        Candidates
                      </label>
                    </div>
                  </>
                ))}
              </>
            ) : (
              races[index].type === 'Race' && (
                <>
                  <div className="w-[2px] bg-[#E6EBF5] h-12" />
                  <div className="flex flex-col w-[84px]">
                    <NumberInput
                      name={`races.${index}.candidates`}
                      register={register as UseFormRegister<AnyForm>}
                      id="candidates"
                      defaultValue={0}
                      disabled={getValues().showCandidateNames}
                      className="w-full h-12 focus:outline-none text-lg px-4 py-3 rounded-md bg-[#E6EBF5] leading-5 text-gray-900 focus:ring-0 disabled:opacity-25 disabled:cursor-not-allowed text-center"
                    />
                    <label
                      htmlFor="candidates"
                      className="text-[#757575] font-light text-sm mt-1"
                    >
                      Write-Ins
                    </label>
                  </div>
                </>
              )
            )}
            {races?.[index].type === 'Race' && (
              <>
                <div className="w-[2px] bg-[#E6EBF5] h-12" />
                <div className="flex flex-col w-[84px]">
                  <NumberInput
                    name={`races.${index}.writeIns`}
                    register={register as UseFormRegister<AnyForm>}
                    id="writeIns"
                    defaultValue={1}
                    className="w-full h-12 focus:outline-none text-lg px-4 py-3 rounded-md bg-[#E6EBF5] leading-5 text-gray-900 focus:ring-0 disabled:opacity-25 disabled:cursor-not-allowed text-center"
                  />
                  <label
                    htmlFor="writeIns"
                    className="text-[#757575] font-light text-sm mt-1"
                  >
                    Write-Ins
                  </label>
                </div>
                <div className="w-[2px] bg-[#E6EBF5] h-12" />
                <div className="flex flex-col w-[84px]">
                  <NumberInput
                    name={`races.${index}.voteFor`}
                    register={register as UseFormRegister<AnyForm>}
                    id="voteFor"
                    defaultValue={1}
                    className="w-full h-12 focus:outline-none text-lg px-4 py-3 rounded-md bg-[#E6EBF5] leading-5 text-gray-900 focus:ring-0 disabled:opacity-25 disabled:cursor-not-allowed text-center"
                  />
                  <label
                    htmlFor="voteFor"
                    className="text-[#757575] font-light text-sm mt-1"
                  >
                    Vote For
                  </label>
                </div>
              </>
            )}
          </div>
        </div>
        {getValues().showCandidateNames &&
        races[index].type === 'Race' &&
        races[index].raceType !== 'Non-Partisan' ? (
          <>
            <div className="flex mt-4">
              <button
                className="uppercase tracking-[0.07rem] justify-center items-center px-6 py-3 text-primary-blue border border-primary-blue text-base font-semibold rounded-md shadow-sm bg-transparent hover:bg-primary-blue hover:text-white focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-gray-500 disabled:cursor-not-allowed disabled:border-gray-500 disabled:text-gray-500 disabled:hover:bg-white"
                onClick={() => {
                  setIsHeadlessOpen(true)
                }}
              >
                Edit Candidate Names
              </button>
              <span
                className={`flex ml-8 ${
                  races[index].candidates === getCountNamesAdded() &&
                  races[index].candidates !== 0
                    ? 'text-green-600'
                    : 'text-red-600'
                } font-semibold justify-center items-center`}
              >
                {`${getCountNamesAdded()} OF ${
                  getValues().races[index].candidates
                } NAMES ADDED`}
              </span>
            </div>
            <SlideOver
              open={isHeadlessOpen}
              setOpen={setIsHeadlessOpen}
              title="Edit Candidate Names"
              subtitle={`${races[index]?.name} Race`}
            >
              <div className="flex flex-col border-gray-300">
                {races[index].parties.map((party, idx) => {
                  return (
                    <div key={party.id}>
                      <AdminManagePartisanChartCandidateNamesItem
                        index={index}
                        partyIndex={idx}
                        adminManagePartisanChartState={
                          adminManagePartisanChartState
                        }
                      />
                    </div>
                  )
                })}
              </div>
            </SlideOver>
          </>
        ) : null}
        {getValues().showCandidateNames &&
        races[index].type === 'Race' &&
        races[index].raceType === 'Non-Partisan' ? (
          <>
            <div className="flex mt-4">
              <button
                className="uppercase tracking-[0.07rem] justify-center items-center px-6 py-3 text-primary-blue border border-primary-blue text-base font-semibold rounded-md shadow-sm bg-transparent hover:bg-primary-blue hover:text-white focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-gray-500 disabled:cursor-not-allowed disabled:border-gray-500 disabled:text-gray-500 disabled:hover:bg-white"
                onClick={() => {
                  setIsHeadlessOpen(true)
                }}
              >
                Edit Candidate Names
              </button>
              <span
                className={`flex ml-8 ${
                  races[index].candidates === getCountNonPartisanNamesAdded() &&
                  races[index].candidates !== 0
                    ? 'text-green-600'
                    : 'text-red-600'
                } font-semibold justify-center items-center`}
              >
                {`${getCountNonPartisanNamesAdded()} OF ${
                  races[index].candidates ?? 0
                } NAMES ADDED`}
              </span>
            </div>
            <SlideOver
              open={isHeadlessOpen}
              setOpen={setIsHeadlessOpen}
              title="Edit Candidate Names"
              subtitle={`${field?.name} Race`}
            >
              <div className="flex flex-col border-gray-300">
                <AdminManagePartisanNonPartisanChartCandidateNamesItem
                  index={index}
                  adminManagePartisanChartState={adminManagePartisanChartState}
                />
              </div>
            </SlideOver>
          </>
        ) : null}
      </div>
    </>
  )
}

export default AdminManagePartisanChartRaceItem
